'use client';

import { useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { DBInquiry } from '@/lib/services/dataService';

interface UseRealtimeInquiriesOptions {
  /** Filter by listing_id — pass undefined to subscribe to all inquiries */
  listingId?: string;
  /** Array of listing IDs owned by the current user — used to filter notifications */
  ownerListingIds?: string[];
  onInsert?: (inquiry: DBInquiry) => void;
}

/**
 * Subscribes to real-time INSERT events on the `inquiries` table.
 * When ownerListingIds is provided, only fires onInsert for inquiries
 * that belong to one of those listings.
 * Automatically cleans up the channel on unmount.
 */
export function useRealtimeInquiries({
  listingId,
  ownerListingIds,
  onInsert,
}: UseRealtimeInquiriesOptions) {
  const onInsertRef = useRef(onInsert);
  onInsertRef.current = onInsert;

  const ownerListingIdsRef = useRef(ownerListingIds);
  ownerListingIdsRef.current = ownerListingIds;

  useEffect(() => {
    const supabase = createClient();
    const channelName = listingId
      ? `inquiries:listing:${listingId}`
      : 'inquiries:all';

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'inquiries',
          ...(listingId ? { filter: `listing_id=eq.${listingId}` } : {}),
        },
        (payload) => {
          const inquiry = payload.new as DBInquiry;
          // If ownerListingIds provided, only fire for owner's own listings
          const ids = ownerListingIdsRef.current;
          if (ids && ids.length > 0 && !ids.includes(inquiry.listing_id)) {
            return;
          }
          onInsertRef.current?.(inquiry);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [listingId]);
}
